import React, { useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Button, IconButton } from '@shopflo/ui'
import SegmentNameEditor from './SegmentNameEditor'
import { useLocale } from 'src/lib/hooks/useLocal'
import SegmentConditionBuilder from './SegmentConditionBuilder'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { Trash } from '@phosphor-icons/react'
import SegmentDefinitionPanelSkeleton from './SegmentDefinitionPanelSkeleton'
import { SegmentTemplateType } from 'src/lib/types/segment'

interface SegmentDefinitionPanelProps {
  /** Host-controlled dismiss (e.g. SSO-analytics overlay). Falls back to discarding the workspace. */
  onCancel?: () => void
  /** Overrides the context's own template application when the host needs to handle it. */
  onTemplateSelect?: (template: SegmentTemplateType) => void
}

const SegmentDefinitionPanel: React.FC<SegmentDefinitionPanelProps> = ({ onCancel, onTemplateSelect }) => {
  const { t } = useLocale()
  const { actions, segmentFormState, isLoadingSegment, isSavingSegment, isEditMode, hasPreviewChanges } =
    useDynamicSegment()
  const { saveSegment, discardWorkspace } = actions
  const hasFilters = segmentFormState.filters.length > 0
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const prevFiltersLengthRef = useRef<number | null>(null)
  const skipScrollAfterSegmentLoadRef = useRef(false)
  /** Only while merging segment/template JSON into the form — not initial schema fetch */
  const showDefinitionSkeleton = isLoadingSegment

  // External sync: after user adds conditions, scroll the panel so the new block + “Add condition” stay in view
  useEffect(() => {
    if (showDefinitionSkeleton) {
      skipScrollAfterSegmentLoadRef.current = true
      return
    }
    if (skipScrollAfterSegmentLoadRef.current) {
      skipScrollAfterSegmentLoadRef.current = false
      prevFiltersLengthRef.current = segmentFormState.filters.length
      return
    }

    const nextLen = segmentFormState.filters.length
    if (prevFiltersLengthRef.current === null) {
      prevFiltersLengthRef.current = nextLen
      return
    }
    if (nextLen > prevFiltersLengthRef.current) {
      const scrollToBottom = () => {
        const el = scrollContainerRef.current
        if (!el) return
        el.scrollTop = el.scrollHeight
      }
      scrollToBottom()
      const t1 = window.setTimeout(scrollToBottom, 120)
      const t2 = window.setTimeout(scrollToBottom, 320)
      prevFiltersLengthRef.current = nextLen
      return () => {
        window.clearTimeout(t1)
        window.clearTimeout(t2)
      }
    }
    prevFiltersLengthRef.current = nextLen
  }, [segmentFormState.filters.length, showDefinitionSkeleton])

  const handleSave = async () => {
    const errorCount = await saveSegment()
    if (errorCount != null && errorCount > 0) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  return (
    <div className='flex h-full flex-col'>
      <div className='relative min-h-0 flex-1'>
        <AnimatePresence mode='popLayout'>
          {showDefinitionSkeleton ? (
            <motion.div
              key='segment-definition-skeleton'
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeOut' } }}
              transition={{ duration: 0 }}
              className='absolute inset-0 flex flex-col overflow-y-auto pt-6'
            >
              <SegmentDefinitionPanelSkeleton />
            </motion.div>
          ) : (
            <motion.div
              key='segment-definition-content'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { duration: 0.28, ease: 'easeOut' } }}
              exit={{ opacity: 1, transition: { duration: 0 } }}
              className='absolute inset-0 flex min-h-0 flex-col pt-6'
            >
              <div
                ref={scrollContainerRef}
                className='flex min-h-0 flex-1 flex-col overflow-y-auto pb-10'
              >
                <div className='px-8 pb-5'>
                  <SegmentNameEditor />
                </div>
                {hasFilters && (
                  <div className='mb-4 flex flex-row items-center gap-2'>
                    <div className='w-6 shrink-0 border-t-2 border-dotted border-zinc-950/10' />
                    <span className='shrink-0 whitespace-nowrap text-text-sm-semibold text-zinc-800'>
                      {t('segment_creation.users_label')}
                    </span>
                    <div className='flex-1 border-t-2 border-dotted border-zinc-950/10' />
                    <IconButton
                      IconComponent={Trash}
                      size='xs'
                      variant='ghost'
                      onClick={() => actions.setSegmentFormState({ ...segmentFormState, filters: [] })}
                    />
                    <div className='w-6 shrink-0 border-t-2 border-dotted border-zinc-950/10' />
                  </div>
                )}
                <div className='w-full min-h-0 flex-1'>
                  <SegmentConditionBuilder onTemplateSelect={onTemplateSelect} />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className='flex flex-col gap-1.5 px-5 pt-3 pb-4'>
        <Button
          id='segment-save-button'
          variant='primary'
          size='md'
          onClick={handleSave}
          loading={isSavingSegment}
          disabled={
            showDefinitionSkeleton || isSavingSegment || (isEditMode && !hasPreviewChanges)
          }
          className='w-full'
        >
          {t('segment_creation.save_segment')}
        </Button>
        <Button
          id='segment-discard-button'
          variant='ghost'
          size='md'
          onClick={() => (onCancel ? onCancel() : discardWorkspace())}
          disabled={isSavingSegment}
          className='w-full'
        >
          {t('segment_creation.discard')}
        </Button>
      </div>
    </div>
  )
}

export default SegmentDefinitionPanel
